import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CrisisDialog } from "@/components/Crisis";
import { Sparkles, Trash2, Heart } from "lucide-react";
import { toast } from "sonner";

const riskStyle = {
  none: "bg-calm text-calm-text",
  low: "bg-calm text-calm-text",
  moderate: "bg-warmsoft text-warmsoft-text",
  high: "bg-warmsoft text-warmsoft-text",
  crisis: "bg-warmsoft text-warmsoft-text",
};

const AnalysisCard = ({ a }) => (
  <div className="mt-3 bg-linen-2 rounded-2xl p-4 space-y-2" data-testid="journal-analysis">
    <div className="flex items-center gap-2 flex-wrap">
      <span className={`text-xs px-3 py-1 rounded-full ${riskStyle[a.risk_level] || "bg-calm text-calm-text"}`}>
        {a.risk_level} attention
      </span>
      {(a.primary_emotions || []).slice(0, 3).map((e, i) => (
        <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-white text-ink-2 border border-linen-3">{e}</span>
      ))}
    </div>
    {a.supportive_reflection && <p className="text-sm text-ink-2 leading-relaxed">{a.supportive_reflection}</p>}
    {a.jungian_insight && <p className="text-sm italic text-brand leading-relaxed font-serif text-base">{a.jungian_insight}</p>}
    {(a.recommended_tools || []).length > 0 && (
      <ul className="text-sm text-ink-2 list-disc list-inside space-y-0.5 pt-1">
        {a.recommended_tools.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
    )}
  </div>
);

const Journal = () => {
  const [entries, setEntries] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [crisisOpen, setCrisisOpen] = useState(false);

  const load = async () => { const { data } = await api.get("/journal"); setEntries(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/journal", { title, content });
      setTitle(""); setContent("");
      await load();
      if (data.crisis) { setCrisisOpen(true); }
      else toast.success("Entry saved. Aurea has reflected on it below.");
    } catch {
      toast.error("Couldn't save your entry. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="journal-page">
      <div>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink">Journal</h1>
        <p className="text-ink-2 mt-2">Write freely. Aurea reads with care, reflects gently, and watches over your wellbeing.</p>
      </div>

      <div className="bg-white rounded-3xl border border-linen-3 p-6 sm:p-8">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="border-0 bg-transparent font-serif text-2xl px-0 focus-visible:ring-0 placeholder:text-ink-mute" data-testid="journal-title" />
        <div className="h-px bg-linen-3 my-3" />
        <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Today I noticed…" className="border-0 bg-transparent resize-none min-h-[180px] px-0 focus-visible:ring-0 text-[15px] leading-loose" data-testid="journal-content" />
        <div className="flex justify-end mt-3">
          <Button onClick={save} disabled={saving || !content.trim()} className="rounded-full bg-brand hover:bg-brand-hover text-linen px-6" data-testid="journal-submit">
            <Sparkles className="w-4 h-4 mr-2" /> {saving ? "Reflecting…" : "Save & reflect"}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {entries.map((e) => (
          <div key={e.id} className="bg-white rounded-3xl border border-linen-3 p-6" data-testid="journal-entry">
            <div className="flex items-start justify-between gap-3">
              <div>
                {e.title && <h3 className="font-serif text-2xl text-ink">{e.title}</h3>}
                <p className="text-xs text-ink-mute">{new Date(e.created_at).toLocaleString()}</p>
              </div>
              <button onClick={async () => { await api.delete(`/journal/${e.id}`); load(); }} className="text-ink-mute hover:text-accent" data-testid="journal-delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <p className="text-ink-2 mt-3 leading-loose whitespace-pre-wrap text-[15px]">{e.content}</p>
            {e.analysis && <AnalysisCard a={e.analysis} />}
          </div>
        ))}
        {entries.length === 0 && (
          <div className="text-center py-16 text-ink-mute">
            <Heart className="w-10 h-10 mx-auto mb-3 text-linen-3" />
            <p>Your reflections will gather here.</p>
          </div>
        )}
      </div>

      <CrisisDialog open={crisisOpen} onOpenChange={setCrisisOpen} />
    </div>
  );
};

export default Journal;
